import {LoadSpacingScenario} from '../../fields/loadSpacing/types'
import {useFormEffect} from '../../form/useFormEffect'

import {useLoadSpacing} from './useLoadSpacing'
import {useLoadSpacingScenario} from './useLoadSpacingScenario'

/**
 * Automatically adjusts load spacing and time on job site fields based on truck scenario changes.
 *
 * This hook monitors the current load spacing scenario (single truck vs multiple trucks) and:
 * - Auto-populates spacing/flow rate fields when switching to multiple trucks
 * - Clears spacing fields when switching to single truck
 * - Adjusts time on job site based on scenario requirements
 *
 * Auto-adjustment only runs when:
 * - Load spacing configuration is enabled
 * - enableAutoAdjustment flag is true (based on order status)
 *
 * @see OrderChangeProcessProvider for order status-based enablement logic
 */
export const useLoadSpacingAdjustment = (isOrderChangeProcess: boolean = false) => {
  const {
    spacingOrFlowRate,
    setSpacingOrFlowRate,
    defaultSpacingOrFlowRate,
    timeOnJobSite,
    setTimeOnJobSite,
    defaultTimeOnJobSite,
    preserveSpacingOrFlowRate,
    loadSpacingConfiguration
  } = useLoadSpacing()
  const currentScenario = useLoadSpacingScenario()

  const resolveTimeOnJobSiteDefault = (): number | undefined => {
    if (isOrderChangeProcess) {
      return defaultTimeOnJobSite?.time
    }

    const configDefault = loadSpacingConfiguration.isEnabled
      ? loadSpacingConfiguration.timeOnJobSite?.default
      : undefined

    return defaultTimeOnJobSite?.time ?? configDefault
  }

  const setSpacingOrFlowRateWithDefaults = (type: 'loadSpace' | 'flowRate') => {
    const currentValue = spacingOrFlowRate?.type === type ? spacingOrFlowRate[type] : undefined
    const defaultValue =
      defaultSpacingOrFlowRate?.type === type ? defaultSpacingOrFlowRate[type] : undefined
    const newValue = currentValue ?? defaultValue ?? loadSpacingConfiguration[type]?.default
    setSpacingOrFlowRate(newValue, type)
  }

  const adjustTimeOnJobSite = (scenario: LoadSpacingScenario) => {
    if (!loadSpacingConfiguration.isEnabled) {
      return
    }

    if (scenario.timeOnJobSite && !timeOnJobSite) {
      const newTimeOnJobSite = resolveTimeOnJobSiteDefault()

      if (newTimeOnJobSite !== undefined) {
        setTimeOnJobSite(newTimeOnJobSite)
      }
    } else if (!scenario.timeOnJobSite && timeOnJobSite) {
      setTimeOnJobSite(undefined)
    }
  }

  const adjustSpacingOrFlowRate = (scenario: LoadSpacingScenario) => {
    if (preserveSpacingOrFlowRate) {
      return
    }

    if (scenario.loadSpace || scenario.flowRate) {
      const hasValidValue =
        (scenario.loadSpace && spacingOrFlowRate?.type === 'loadSpace') ||
        (scenario.flowRate && spacingOrFlowRate?.type === 'flowRate')
      if (hasValidValue) {
        return
      }

      const hasValidDefaultFlowRate =
        scenario.flowRate && defaultSpacingOrFlowRate?.type === 'flowRate'

      if (scenario.loadSpace && !hasValidDefaultFlowRate) {
        setSpacingOrFlowRateWithDefaults('loadSpace')
      } else {
        setSpacingOrFlowRateWithDefaults('flowRate')
      }
    } else if (spacingOrFlowRate) {
      setSpacingOrFlowRate(undefined)
    }
  }

  useFormEffect(() => {
    // Skip auto-adjustment when disabled or for order statuses where manual changes are required
    if (!loadSpacingConfiguration.isEnabled || !loadSpacingConfiguration.enableAutoAdjustment) {
      return
    }
    adjustSpacingOrFlowRate(currentScenario)
    adjustTimeOnJobSite(currentScenario)
  }, [currentScenario, loadSpacingConfiguration, preserveSpacingOrFlowRate])
}
